import React, { useState, useEffect, useMemo } from 'react';
import { Download, FileText, Filter } from 'lucide-react';
import useAutoRefresh from '../../lib/useAutoRefresh';
import RepeatOffendersTable from './RepeatOffendersTable';
import AbsentRepeatOffendersTable from './AbsentRepeatOffendersTable';
import FilterableDataTable from './FilterableDataTable';
import GeoFenceMultiSelect from './GeoFenceMultiSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_TYPES = [
  { id: 'supplier_visits', label: 'Supplier Visit Report' },
  { id: 'staff_visits', label: 'Staff Visit Report' },
  { id: 'geofence_status', label: 'Geofence Status' },
  { id: 'outside_checkin', label: 'Outside Check-in Report' },
  { id: 'outside_checkout', label: 'Outside Check-out Report' },
  { id: 'lunch_excess', label: 'Lunch Excess Report' },
  { id: 'permissions', label: 'Permission Report' },
  { id: 'non_plan_hours', label: 'Non-Plan Visit Hours' },
  { id: 'attendance', label: 'Attendance Report' },
  { id: 'late_comers', label: 'Late Comers Review' },
  { id: 'absent_leave', label: 'Absent & Leave Review' },
  { id: 'unplan_visit', label: 'Unplan Visit Review' },
  { id: 'mobile_hours', label: 'Mobile Hours Report' },
  { id: 'out_of_office', label: 'Out of Office (Unauthorised)' }
];

export default function Reports() {
  const [active, setActive] = useState('attendance');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Multi-select geofence filter (June 9, 2026 user ask). Only surfaced for
  // late_comers / absent_leave tabs but the backend honours it on every
  // report type, so we keep the state at the page level.
  const [geoFenceIds, setGeoFenceIds] = useState([]);
  const [geoFences, setGeoFences] = useState([]);
  const [data, setData] = useState({ rows: [], count: 0 });
  const [loading, setLoading] = useState(false);
  // The post-filter rows bubbled up from FilterableDataTable — used by
  // Download Excel so the export respects whatever the admin sees on
  // screen. (June 18, 2026 user ask.)
  const [filteredRows, setFilteredRows] = useState(null);
  const supportsGeoFilter = active === 'late_comers' || active === 'absent_leave' || active === 'unplan_visit';

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, geoFenceIds]);

  // Load the geofence master once (used by the multi-select picker).
  useEffect(() => {
    const loadGeo = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/geo-fences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const list = await r.json();
          // Sort alphabetically — the picker also has type-to-filter so
          // alphabetical helps an admin who's scanning visually.
          setGeoFences([...list].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        }
      } catch (e) {
        console.error('[Reports] geofence load', e);
      }
    };
    loadGeo();
  }, []);

  const buildUrl = (suffix = '') => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (supportsGeoFilter) {
      for (const id of geoFenceIds) params.append('geo_fence_id', id);
    }
    const qs = params.toString();
    return `${API_URL}/api/admin/reports/${active}${suffix}${qs ? '?' + qs : ''}`;
  };

  const fetchReport = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(buildUrl(''), { headers: { 'Authorization': `Bearer ${token}` } });
      const d = await r.json();
      setData(d);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Silent 30-sec auto-refresh — preserves filter selection, no flicker.
  useAutoRefresh(() => fetchReport(true), 30_000);

  const handleExport = async () => {
    // If the in-memory filtered rows differ from the raw server rows,
    // the admin has narrowed the table on screen — export EXACTLY that
    // visible slice client-side so the Excel file mirrors the UI. When
    // no filter is applied we fall back to the server-side export which
    // preserves the totals row used by the claim_fortnight report.
    const visible = filteredRows;
    const allRows = data.rows || [];
    const filterApplied =
      Array.isArray(visible) && visible.length !== allRows.length;

    if (filterApplied) {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.json_to_sheet(visible);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, active.slice(0, 30));
      const ts = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      XLSX.writeFile(wb, `carloo_${active}_filtered_${ts}.xlsx`);
      return;
    }

    const token = localStorage.getItem('token');
    const r = await fetch(buildUrl('/export'), { headers: { 'Authorization': `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `carloo_${active}_${Date.now()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Late-Comers grouped summary --------------------------------------
  // Aggregate the raw rows by Geofence and Department so the admin can spot
  // which team is consistently coming in late without scanning hundreds of
  // individual rows. Each cell is the count of late-arrival incidents (one
  // row = one staff-on-one-day).
  // Also flag "repeat offenders" — any staff with REPEAT_THRESHOLD or more
  // late incidents in the selected range. They get a red badge in the
  // detail table + their own summary card.
  const REPEAT_THRESHOLD = 5;
  const lateSummary = useMemo(() => {
    if (active !== 'late_comers' || !data.rows || data.rows.length === 0) return null;
    const byGeo = new Map();
    const byDept = new Map();
    const byStaff = new Map();
    const buckets = ['0-10 m', '10-20 m', '20-30 m', '30-45 m', '45-60 m', '> 1 hr'];
    const emptyCounts = () => Object.fromEntries(buckets.map(b => [b, 0]));
    for (const row of data.rows) {
      const g = row.Geofence || '(Unassigned)';
      const d = row.Department || '-';
      const sid = row['Staff ID'] || row.Staff;
      const b = row['Late Bucket'] || '> 1 hr';
      if (!byGeo.has(g))  byGeo.set(g,  { name: g, total: 0, ...emptyCounts() });
      if (!byDept.has(d)) byDept.set(d, { name: d, total: 0, ...emptyCounts() });
      if (!byStaff.has(sid)) byStaff.set(sid, {
        sid, name: row.Staff, geofence: g, department: d,
        total: 0, total_late_min: 0, worst_min: 0, ...emptyCounts(),
      });
      byGeo.get(g).total += 1;
      byGeo.get(g)[b] = (byGeo.get(g)[b] || 0) + 1;
      byDept.get(d).total += 1;
      byDept.get(d)[b] = (byDept.get(d)[b] || 0) + 1;
      const s = byStaff.get(sid);
      const mins = Number(row['Late By (min)']) || 0;
      s.total += 1;
      s.total_late_min += mins;
      if (mins > s.worst_min) s.worst_min = mins;
      s[b] = (s[b] || 0) + 1;
    }
    const sortByTotal = (a, b) => b.total - a.total || a.name.localeCompare(b.name);
    const repeatOffenders = Array.from(byStaff.values())
      .filter(s => s.total >= REPEAT_THRESHOLD)
      .sort(sortByTotal);
    const repeatSidSet = new Set(repeatOffenders.map(s => s.sid));

    // Daily trend — count of late-check-in incidents per date in the range.
    // We sort lexicographically since dates are YYYY-MM-DD ISO strings.
    const byDate = new Map();
    for (const row of data.rows) {
      const d = row.Date || (row['Check-in'] || '').slice(0, 10);
      if (!d) continue;
      byDate.set(d, (byDate.get(d) || 0) + 1);
    }
    const trend = Array.from(byDate.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Bucket distribution — across the whole report.
    const bucketDist = buckets.map(b => {
      const count = data.rows.reduce((sum, r) => sum + ((r['Late Bucket'] || '> 1 hr') === b ? 1 : 0), 0);
      return { bucket: b, count, pct: data.rows.length ? (count / data.rows.length) * 100 : 0 };
    });

    return {
      buckets,
      geofences: Array.from(byGeo.values()).sort(sortByTotal),
      departments: Array.from(byDept.values()).sort(sortByTotal),
      repeatOffenders,
      repeatSidSet,
      trend,
      bucketDist,
      totalIncidents: data.rows.length,
      uniqueStaff: byStaff.size,
      threshold: REPEAT_THRESHOLD,
    };
  }, [active, data.rows]);

  // ---- Absent & Leave grouped summary ------------------------------------
  // Mirrors lateSummary but for the absent_leave report. Threshold is 2
  // (user ask, May 29, 2026 — "Keep +2 days as alert"). Even staff with 1
  // day in range still appear in the main row list — that's handled by the
  // generic data.rows render, not here. Here we only highlight repeats.
  const ABSENT_REPEAT_THRESHOLD = 2;
  const absentSummary = useMemo(() => {
    if (active !== 'absent_leave' || !data.rows || data.rows.length === 0) return null;
    const TYPE_BUCKETS = ['Absent', 'Leave (Approved)', 'Leave (Pending)', 'Leave (Rejected)'];
    const emptyCounts = () => Object.fromEntries(TYPE_BUCKETS.map(b => [b, 0]));
    const byGeo = new Map();
    const byDept = new Map();
    const byStaff = new Map();
    for (const row of data.rows) {
      const g = row.Geofence || '(Unassigned)';
      const d = row.Department || '-';
      const sid = row['Staff ID'] || row.Staff;
      const t = row.Type || 'Absent';
      const tKey = TYPE_BUCKETS.includes(t) ? t : 'Absent';
      if (!byGeo.has(g))  byGeo.set(g,  { name: g, total: 0, ...emptyCounts() });
      if (!byDept.has(d)) byDept.set(d, { name: d, total: 0, ...emptyCounts() });
      if (!byStaff.has(sid)) byStaff.set(sid, {
        sid, name: row.Staff, geofence: g, department: d,
        total: 0, absent: 0, leave_approved: 0, leave_pending: 0, leave_rejected: 0,
        // last_date is the most-recent occurrence — useful when admin asks
        // "when was the last time X was absent?"
        last_date: row.Date || '',
      });
      byGeo.get(g).total += 1;
      byGeo.get(g)[tKey] = (byGeo.get(g)[tKey] || 0) + 1;
      byDept.get(d).total += 1;
      byDept.get(d)[tKey] = (byDept.get(d)[tKey] || 0) + 1;
      const s = byStaff.get(sid);
      s.total += 1;
      if (tKey === 'Absent') s.absent += 1;
      else if (tKey === 'Leave (Approved)') s.leave_approved += 1;
      else if (tKey === 'Leave (Pending)') s.leave_pending += 1;
      else if (tKey === 'Leave (Rejected)') s.leave_rejected += 1;
      if ((row.Date || '') > s.last_date) s.last_date = row.Date || '';
    }
    const sortByTotal = (a, b) => b.total - a.total || a.name.localeCompare(b.name);
    const repeatOffenders = Array.from(byStaff.values())
      .filter(s => s.total >= ABSENT_REPEAT_THRESHOLD)
      .sort(sortByTotal);
    const repeatSidSet = new Set(repeatOffenders.map(s => s.sid));

    // Daily trend — count of absent+leave incidents per date.
    const byDate = new Map();
    for (const row of data.rows) {
      const d = row.Date;
      if (!d) continue;
      byDate.set(d, (byDate.get(d) || 0) + 1);
    }
    const trend = Array.from(byDate.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    // Type breakdown across the whole report (for the bucket-distribution card).
    const typeDist = TYPE_BUCKETS.map(b => {
      const count = data.rows.reduce((sum, r) => sum + ((r.Type || 'Absent') === b ? 1 : 0), 0);
      return { bucket: b, count, pct: data.rows.length ? (count / data.rows.length) * 100 : 0 };
    });

    return {
      buckets: TYPE_BUCKETS,
      geofences: Array.from(byGeo.values()).sort(sortByTotal),
      departments: Array.from(byDept.values()).sort(sortByTotal),
      repeatOffenders,
      repeatSidSet,
      trend,
      typeDist,
      totalIncidents: data.rows.length,
      uniqueStaff: byStaff.size,
      threshold: ABSENT_REPEAT_THRESHOLD,
    };
  }, [active, data.rows]);

  // ---- Unplan Visit grouped summary --------------------------------------
  // Mirrors lateSummary but for unplan_visit. Threshold is 2 (anything is
  // worth flagging in a small sample). Repeat-offender pill in the row
  // table relies on `repeatSidSet` like the other reports.
  const UNPLAN_REPEAT_THRESHOLD = 2;
  const unplanSummary = useMemo(() => {
    if (active !== 'unplan_visit' || !data.rows || data.rows.length === 0) return null;
    const byStaff = new Map();
    const byGeo = new Map();
    const byDept = new Map();
    let totalMinutes = 0;
    for (const row of data.rows) {
      const sid = row['Staff ID'] || row.Staff;
      const g = row.Geofence || '(Unassigned)';
      const d = row.Department || '-';
      const mins = Number(row['Duration (min)']) || 0;
      totalMinutes += mins;
      if (!byStaff.has(sid)) {
        byStaff.set(sid, {
          sid, name: row.Staff, geofence: g, department: d,
          total: 0, total_minutes: 0, pending: 0, approved: 0,
          last_date: row.Date || '',
        });
      }
      const s = byStaff.get(sid);
      s.total += 1;
      s.total_minutes += mins;
      const ap = (row.Approval || 'pending').toLowerCase();
      if (ap.startsWith('approved') || ap.startsWith('waiv')) s.approved += 1;
      else s.pending += 1;
      if ((row.Date || '') > s.last_date) s.last_date = row.Date || '';
      if (!byGeo.has(g)) byGeo.set(g, { name: g, total: 0, total_minutes: 0 });
      byGeo.get(g).total += 1; byGeo.get(g).total_minutes += mins;
      if (!byDept.has(d)) byDept.set(d, { name: d, total: 0, total_minutes: 0 });
      byDept.get(d).total += 1; byDept.get(d).total_minutes += mins;
    }
    const sortByTotal = (a, b) => b.total - a.total || a.name.localeCompare(b.name);
    const repeatOffenders = Array.from(byStaff.values())
      .filter(s => s.total >= UNPLAN_REPEAT_THRESHOLD)
      .sort(sortByTotal);
    const repeatSidSet = new Set(repeatOffenders.map(s => s.sid));

    // Daily trend
    const byDate = new Map();
    for (const row of data.rows) {
      const d = row.Date; if (!d) continue;
      byDate.set(d, (byDate.get(d) || 0) + 1);
    }
    const trend = Array.from(byDate.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return {
      geofences: Array.from(byGeo.values()).sort(sortByTotal),
      departments: Array.from(byDept.values()).sort(sortByTotal),
      repeatOffenders,
      repeatSidSet,
      trend,
      totalIncidents: data.rows.length,
      totalMinutes,
      uniqueStaff: byStaff.size,
      threshold: UNPLAN_REPEAT_THRESHOLD,
    };
  }, [active, data.rows]);

  return (
    <div data-testid="reports-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reports & Excel Export</h1>
        <button
          onClick={handleExport}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
          data-testid="export-xlsx-btn"
        >
          <Download size={18} /><span>Download Excel</span>
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {REPORT_TYPES.map(t => (
          <button
            key={t.id}
            onClick={() => {
              // Switching tab clears the geofence pick so the new tab
              // starts unfiltered. Date range is intentionally kept
              // because admins often jump between Late Comers and
              // Absent & Leave for the same period.
              setGeoFenceIds([]);
              setActive(t.id);
            }}
            className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              active === t.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
            }`}
            data-testid={`report-tab-${t.id}`}
          >
            <FileText size={14} className="inline mr-1" />{t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl p-4 shadow-sm border mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start date</label>
            <input
              data-testid="report-start-date"
              type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">End date</label>
            <input
              data-testid="report-end-date"
              type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm"
            />
          </div>
          {supportsGeoFilter && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Geo-Fence
                <span className="text-gray-400 font-normal"> (multi-select)</span>
              </label>
              <GeoFenceMultiSelect
                options={geoFences}
                selectedIds={geoFenceIds}
                onChange={setGeoFenceIds}
              />
            </div>
          )}
          <button
            onClick={fetchReport}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm flex items-center space-x-1"
            data-testid="apply-filter-btn"
          >
            <Filter size={14} /><span>Apply</span>
          </button>
          <span className="text-xs text-gray-500 ml-auto">
            {loading ? 'Loading...' : `${data.count || 0} rows`}
          </span>
        </div>
      </div>

      {lateSummary && (
        <div className="grid md:grid-cols-2 gap-3 mb-4" data-testid="late-comers-summary">
          <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center justify-between text-sm flex-wrap gap-2">
            <div>
              <span className="text-amber-900 font-semibold">{lateSummary.totalIncidents}</span>
              <span className="text-amber-800"> late-check-in incidents</span>
              <span className="text-amber-800"> · </span>
              <span className="text-amber-900 font-semibold">{lateSummary.uniqueStaff}</span>
              <span className="text-amber-800"> unique staff</span>
              {(startDate || endDate) && (
                <span className="text-amber-700 text-xs"> · {startDate || '…'} → {endDate || '…'}</span>
              )}
            </div>
            {lateSummary.repeatOffenders.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 bg-red-100 text-red-800 border border-red-200 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                data-testid="repeat-offenders-pill"
              >
                <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full" />
                {lateSummary.repeatOffenders.length} repeat offender{lateSummary.repeatOffenders.length === 1 ? '' : 's'}
                <span className="text-red-600 font-normal">(≥ {lateSummary.threshold} times late)</span>
              </span>
            )}
          </div>

          <TrendCard trend={lateSummary.trend} totalIncidents={lateSummary.totalIncidents} />
          <BucketDistributionCard bucketDist={lateSummary.bucketDist} totalIncidents={lateSummary.totalIncidents} />

          {lateSummary.repeatOffenders.length > 0 && (
            <RepeatOffendersTable
              offenders={lateSummary.repeatOffenders}
              threshold={lateSummary.threshold}
              grandTotal={lateSummary.totalIncidents}
            />
          )}

          <LateGroupCard
            title="By Geofence — who comes late from which location?"
            rows={lateSummary.geofences}
            buckets={lateSummary.buckets}
            grandTotal={lateSummary.totalIncidents}
            testid="late-summary-geo"
          />
          <LateGroupCard
            title="By Department — which team comes late most often?"
            rows={lateSummary.departments}
            buckets={lateSummary.buckets}
            grandTotal={lateSummary.totalIncidents}
            testid="late-summary-dept"
          />
        </div>
      )}

      {absentSummary && (
        <div className="grid md:grid-cols-2 gap-3 mb-4" data-testid="absent-leave-summary">
          <div className="md:col-span-2 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center justify-between text-sm flex-wrap gap-2">
            <div>
              <span className="text-orange-900 font-semibold">{absentSummary.totalIncidents}</span>
              <span className="text-orange-800"> absent / leave incidents</span>
              <span className="text-orange-800"> · </span>
              <span className="text-orange-900 font-semibold">{absentSummary.uniqueStaff}</span>
              <span className="text-orange-800"> unique staff</span>
              {(startDate || endDate) && (
                <span className="text-orange-700 text-xs"> · {startDate || '…'} → {endDate || '…'}</span>
              )}
            </div>
            {absentSummary.repeatOffenders.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 bg-red-100 text-red-800 border border-red-200 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                data-testid="absent-repeat-offenders-pill"
              >
                <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full" />
                {absentSummary.repeatOffenders.length} repeat offender{absentSummary.repeatOffenders.length === 1 ? '' : 's'}
                <span className="text-red-600 font-normal">(≥ {absentSummary.threshold} days off)</span>
              </span>
            )}
          </div>

          <TrendCard trend={absentSummary.trend} totalIncidents={absentSummary.totalIncidents} />
          <BucketDistributionCard bucketDist={absentSummary.typeDist} totalIncidents={absentSummary.totalIncidents} />

          {absentSummary.repeatOffenders.length > 0 && (
            <AbsentRepeatOffendersTable
              offenders={absentSummary.repeatOffenders}
              threshold={absentSummary.threshold}
              grandTotal={absentSummary.totalIncidents}
            />
          )}

          <LateGroupCard
            title="By Geofence — which location has the most absences?"
            rows={absentSummary.geofences}
            buckets={absentSummary.buckets}
            grandTotal={absentSummary.totalIncidents}
            testid="absent-summary-geo"
          />
          <LateGroupCard
            title="By Department — which team is absent most?"
            rows={absentSummary.departments}
            buckets={absentSummary.buckets}
            grandTotal={absentSummary.totalIncidents}
            testid="absent-summary-dept"
          />
        </div>
      )}

      {unplanSummary && (
        <div className="space-y-3 mb-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="unplan-summary-card">
            <div className="flex-1 text-sm">
              <span className="text-red-900 font-semibold">{unplanSummary.totalIncidents}</span>
              <span className="text-red-800"> unplan exits</span>
              <span className="text-red-800"> · </span>
              <span className="text-red-900 font-semibold">{unplanSummary.uniqueStaff}</span>
              <span className="text-red-800"> unique staff</span>
              <span className="text-red-800"> · </span>
              <span className="text-red-900 font-semibold">
                {Math.floor(unplanSummary.totalMinutes / 60)}h {unplanSummary.totalMinutes % 60}m
              </span>
              <span className="text-red-800"> total time outside</span>
              {(startDate || endDate) && (
                <span className="text-red-700 text-xs"> · {startDate || '…'} → {endDate || '…'}</span>
              )}
            </div>
            {unplanSummary.repeatOffenders.length > 0 && (
              <span
                className="inline-flex items-center gap-1.5 bg-red-200 text-red-900 border border-red-300 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                data-testid="unplan-repeat-offenders-pill"
              >
                <span className="inline-block w-1.5 h-1.5 bg-red-600 rounded-full" />
                {unplanSummary.repeatOffenders.length} repeat offender{unplanSummary.repeatOffenders.length === 1 ? '' : 's'}
                <span className="text-red-700 font-normal">(≥ {unplanSummary.threshold} exits)</span>
              </span>
            )}
          </div>

          <TrendCard trend={unplanSummary.trend} totalIncidents={unplanSummary.totalIncidents} />

          <UnplanCleanupCard onApplied={() => fetchReport()} />

          <SubMinuteCleanupCard onApplied={() => fetchReport()} />

          {unplanSummary.repeatOffenders.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="unplan-repeat-table">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-600">
                Repeat offenders — staff with {unplanSummary.threshold}+ unplan exits in this range
              </div>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-600">
                  <tr className="text-left">
                    <th className="px-3 py-1.5">Staff</th>
                    <th className="px-3 py-1.5">Staff ID</th>
                    <th className="px-3 py-1.5">Geofence</th>
                    <th className="px-3 py-1.5">Department</th>
                    <th className="px-3 py-1.5 text-right">Exits</th>
                    <th className="px-3 py-1.5 text-right">Time outside</th>
                    <th className="px-3 py-1.5 text-right">Pending</th>
                    <th className="px-3 py-1.5 text-right">Approved</th>
                    <th className="px-3 py-1.5">Last date</th>
                  </tr>
                </thead>
                <tbody>
                  {unplanSummary.repeatOffenders.map((s) => (
                    <tr key={s.sid} className="border-t border-gray-100" data-testid={`unplan-offender-${s.sid}`}>
                      <td className="px-3 py-1.5 font-semibold">{s.name}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{s.sid}</td>
                      <td className="px-3 py-1.5 text-xs">{s.geofence}</td>
                      <td className="px-3 py-1.5 text-xs">{s.department}</td>
                      <td className="px-3 py-1.5 text-right font-semibold">{s.total}</td>
                      <td className="px-3 py-1.5 text-right text-xs">
                        {Math.floor(s.total_minutes / 60)}h {s.total_minutes % 60}m
                      </td>
                      <td className="px-3 py-1.5 text-right text-amber-700">{s.pending}</td>
                      <td className="px-3 py-1.5 text-right text-emerald-700">{s.approved}</td>
                      <td className="px-3 py-1.5 text-xs">{s.last_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <FilterableDataTable
        rows={data.rows || []}
        repeatSidSet={
          active === 'late_comers' ? lateSummary?.repeatSidSet :
          active === 'absent_leave' ? absentSummary?.repeatSidSet :
          active === 'unplan_visit' ? unplanSummary?.repeatSidSet :
          undefined
        }
        repeatBadgeTitle={
          active === 'late_comers' ? `Late ${REPEAT_THRESHOLD}+ times in this range` :
          active === 'absent_leave' ? `Absent / on leave ${ABSENT_REPEAT_THRESHOLD}+ days in this range` :
          active === 'unplan_visit' ? `${UNPLAN_REPEAT_THRESHOLD}+ unplan exits in this range` :
          'Repeat offender'
        }
        emptyMessage="No rows for selected filters"
        testIdPrefix="report"
        onFilteredRowsChange={setFilteredRows}
      />
    </div>
  );
}

/**
 * Inline SVG mini-chart of daily late-check-in counts.
 * Renders one bar per day present in the data (no empty-day padding —
 * keeps the chart readable when the range is wide and sparse).
 */
function TrendCard({ trend, totalIncidents }) {
  if (!trend || trend.length === 0) return null;
  const max = Math.max(1, ...trend.map(t => t.count));
  // Up to 14 days fit comfortably; longer ranges scroll horizontally.
  const W = 24; // px per bar
  const H = 80;
  const PAD_TOP = 6;
  const avg = totalIncidents / trend.length;
  const peak = trend.reduce((m, t) => (t.count > m.count ? t : m), trend[0]);
  return (
    <div
      className="md:col-span-2 bg-white rounded-xl border shadow-sm overflow-hidden"
      data-testid="late-trend-card"
    >
      <div className="px-4 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-800 flex items-center justify-between flex-wrap gap-2">
        <span>Daily Trend — late-check-in incidents over {trend.length} day{trend.length === 1 ? '' : 's'}</span>
        <span className="text-[11px] font-normal text-gray-500">
          Peak <strong className="text-amber-700">{peak.count}</strong> on {peak.date}
          <span className="mx-1">·</span>
          Avg <strong className="text-amber-700">{avg.toFixed(1)}</strong>/day
        </span>
      </div>
      <div className="overflow-x-auto px-3 py-3">
        <svg
          width={trend.length * W}
          height={H + 28}
          className="block"
          data-testid="late-trend-svg"
        >
          {/* Baseline */}
          <line x1={0} y1={H + PAD_TOP} x2={trend.length * W} y2={H + PAD_TOP} stroke="#e5e7eb" />
          {trend.map((t, i) => {
            const h = max > 0 ? Math.max(2, (t.count / max) * H) : 2;
            const x = i * W + 4;
            const y = H + PAD_TOP - h;
            const isPeak = t.date === peak.date;
            return (
              <g key={t.date}>
                <rect
                  x={x} y={y} width={W - 8} height={h}
                  rx={2}
                  fill={isPeak ? '#d97706' : '#f59e0b'}
                  data-testid={`late-trend-bar-${t.date}`}
                />
                <text
                  x={x + (W - 8) / 2} y={y - 3}
                  textAnchor="middle"
                  className="fill-amber-900"
                  style={{ fontSize: '10px', fontWeight: 600 }}
                >{t.count}</text>
                <text
                  x={x + (W - 8) / 2} y={H + PAD_TOP + 12}
                  textAnchor="middle"
                  className="fill-gray-500"
                  style={{ fontSize: '9px' }}
                >{t.date.slice(5)}</text>
                <text
                  x={x + (W - 8) / 2} y={H + PAD_TOP + 22}
                  textAnchor="middle"
                  className="fill-gray-400"
                  style={{ fontSize: '8px' }}
                >{new Date(t.date).toLocaleDateString('en-IN', { weekday: 'short' })}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

/**
 * Per-bucket distribution card — answers "what share of late check-ins
 * were 0-10m late vs 10-20m vs … > 1hr?".
 */
function BucketDistributionCard({ bucketDist, totalIncidents }) {
  if (!bucketDist || bucketDist.length === 0 || totalIncidents === 0) return null;
  const COLORS = {
    '0-10 m':  'bg-emerald-500',
    '10-20 m': 'bg-yellow-500',
    '20-30 m': 'bg-amber-500',
    '30-45 m': 'bg-orange-500',
    '45-60 m': 'bg-red-500',
    '> 1 hr':  'bg-red-700',
  };
  return (
    <div
      className="md:col-span-2 bg-white rounded-xl border shadow-sm overflow-hidden"
      data-testid="late-bucket-distribution-card"
    >
      <div className="px-4 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-800">
        Bucket distribution — how late were they?
      </div>
      <div className="p-3 space-y-2">
        {bucketDist.map(b => (
          <div key={b.bucket} className="flex items-center gap-3" data-testid={`late-bucket-${b.bucket}`}>
            <span className="w-16 text-xs font-medium text-gray-700">{b.bucket}</span>
            <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
              <div
                className={`h-full ${COLORS[b.bucket] || 'bg-gray-500'} transition-all`}
                style={{ width: `${b.pct.toFixed(1)}%` }}
              />
            </div>
            <span className="w-12 text-xs text-right tabular-nums font-semibold text-gray-800">{b.count}</span>
            <span className="w-14 text-xs text-right tabular-nums font-bold text-amber-700">{b.pct.toFixed(1)}%</span>
          </div>
        ))}
        <p className="text-[11px] text-gray-500 mt-2">
          Total incidents: <strong className="text-gray-700">{totalIncidents}</strong>
        </p>
      </div>
    </div>
  );
}

/**
 * Compact bucket-breakdown card for the Late Comers Review summary.
 * Renders one row per group (geofence OR department) with bucketed counts,
 * a "% of total" column, and a footer TOTAL row summing each bucket.
 */
function LateGroupCard({ title, rows, buckets, grandTotal, testid }) {
  // Column-wise totals across all groups in this card.
  const bucketTotals = buckets.reduce((acc, b) => {
    acc[b] = rows.reduce((sum, r) => sum + (r[b] || 0), 0);
    return acc;
  }, {});
  const cardTotal = rows.reduce((sum, r) => sum + r.total, 0);

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-800">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Group</th>
              <th className="px-3 py-2 text-center text-[10px] font-medium text-gray-500 uppercase">Total</th>
              <th className="px-3 py-2 text-center text-[10px] font-medium text-gray-500 uppercase">% of Total</th>
              {buckets.map(b => (
                <th key={b} className="px-2 py-2 text-center text-[10px] font-medium text-gray-500 uppercase whitespace-nowrap">{b}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr><td colSpan={buckets.length + 3} className="px-3 py-4 text-center text-gray-400">No late incidents in this range.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.name} data-testid={`${testid}-row-${r.name}`}>
                <td className="px-3 py-1.5 text-gray-800 font-medium truncate max-w-[180px]" title={r.name}>{r.name}</td>
                <td className="px-3 py-1.5 text-center font-semibold text-amber-700 tabular-nums">{r.total}</td>
                <td className="px-3 py-1.5 text-center tabular-nums text-amber-700">
                  {grandTotal > 0 ? `${((r.total / grandTotal) * 100).toFixed(1)}%` : '—'}
                </td>
                {buckets.map(b => (
                  <td key={b} className={`px-2 py-1.5 text-center tabular-nums ${r[b] > 0 ? 'text-gray-700' : 'text-gray-300'}`}>{r[b] || 0}</td>
                ))}
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr data-testid={`${testid}-totals`}>
                <td className="px-3 py-2 text-right text-[11px] font-bold text-gray-800 uppercase">Total</td>
                <td className="px-3 py-2 text-center font-bold text-gray-900 tabular-nums">{cardTotal}</td>
                <td className="px-3 py-2 text-center font-bold text-gray-900 tabular-nums">
                  {grandTotal > 0 ? `${((cardTotal / grandTotal) * 100).toFixed(1)}%` : '—'}
                </td>
                {buckets.map(b => (
                  <td key={b} className="px-2 py-2 text-center font-bold tabular-nums text-gray-900">{bucketTotals[b]}</td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}


// --------------------------------------------------------------------------- //
// One-shot cleanup card: scans pending Unplan Visits whose window overlaps a //
// same-day lunch / break / permission and auto-approves them with policy     //
// notes. Dry-run first, then explicit Apply. (Jun 18, 2026 user ask.)        //
// --------------------------------------------------------------------------- //
function UnplanCleanupCard({ onApplied }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [appliedResult, setAppliedResult] = useState(null);

  const scan = async () => {
    setBusy(true); setError(''); setAppliedResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/cleanup/unplan-during-breaks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setPreview(data);
    } catch (e) { setError(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview || (preview.candidates || []).length === 0) return;
    const n = preview.candidates.length;
    if (!window.confirm(`Auto-approve ${n} Unplan Visit row${n === 1 ? '' : 's'} that overlap a same-day lunch / break / permission?\n\nThis is reversible only by manually editing each row.`)) return;
    setApplying(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/cleanup/unplan-during-breaks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirm: true }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setAppliedResult(data);
      setPreview(null);
      if (onApplied) onApplied();
    } catch (e) { setError(e.message || String(e)); }
    finally { setApplying(false); }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="unplan-cleanup-card">
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex-1 text-sm">
          <div className="font-semibold text-blue-900">One-shot cleanup — auto-approve lunch / break false-positives</div>
          <div className="text-xs text-blue-800 mt-0.5">
            Scans every <strong>pending</strong> Unplan Visit and auto-approves any row whose time window overlaps a same-day lunch_records / breaks / permission entry. Idempotent — safe to run again later.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={scan}
            disabled={busy || applying}
            className="px-3 py-1.5 text-sm bg-white border border-blue-300 hover:bg-blue-100 text-blue-800 rounded-lg flex items-center gap-1"
            data-testid="unplan-cleanup-scan-btn"
          >
            {busy ? '…' : 'Scan (dry run)'}
          </button>
          {preview && (preview.candidates?.length ?? 0) > 0 && (
            <button
              onClick={apply}
              disabled={applying}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg flex items-center gap-1"
              data-testid="unplan-cleanup-apply-btn"
            >
              {applying ? 'Applying…' : `Apply (${preview.candidates.length})`}
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">{error}</div>}

      {preview && (
        <div className="mt-3 text-xs">
          <div className="text-blue-900">
            Scanned <strong>{preview.stats.examined_pending}</strong> pending rows · <strong>{preview.stats.candidates}</strong> match {Object.keys(preview.stats.by_kind || {}).length > 0 && (
              <> — by kind: {Object.entries(preview.stats.by_kind).map(([k, n]) => `${k}=${n}`).join(', ')}</>
            )}
          </div>
          {(preview.candidates || []).length > 0 && (
            <div className="mt-2 max-h-56 overflow-y-auto bg-white border border-blue-100 rounded">
              <table className="min-w-full text-xs">
                <thead className="bg-blue-50/60 sticky top-0">
                  <tr className="text-left text-blue-900">
                    <th className="px-2 py-1">Staff</th>
                    <th className="px-2 py-1">Date</th>
                    <th className="px-2 py-1">Match</th>
                    <th className="px-2 py-1">Visit start</th>
                    <th className="px-2 py-1">Visit end</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.candidates.map((c) => (
                    <tr key={c.visit_id} className="border-t border-blue-100">
                      <td className="px-2 py-1">{c.staff_name} <span className="text-gray-500">({c.staff_code})</span></td>
                      <td className="px-2 py-1 font-mono">{c.date}</td>
                      <td className="px-2 py-1"><span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-900">{c.match_kind}</span></td>
                      <td className="px-2 py-1 font-mono text-[10px]">{c.start}</td>
                      <td className="px-2 py-1 font-mono text-[10px]">{c.end || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {appliedResult && (
        <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded p-2" data-testid="unplan-cleanup-applied">
          Approved <strong>{appliedResult.applied}</strong> row{appliedResult.applied === 1 ? '' : 's'} · skipped {appliedResult.skipped}. Run the scan again anytime to clean up new false-positives.
        </div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------------- //
// Jun 26, 2026 — second-stage cleanup: hard-delete pending Unplan Visit rows  //
// whose start↔end is less than 60 s apart. These are GPS jitter false        //
// positives (`when_left == when_returned` rows). The runtime gates now       //
// prevent new ones, but admins still need to purge the historical backlog.   //
// --------------------------------------------------------------------------- //
function SubMinuteCleanupCard({ onApplied }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [appliedResult, setAppliedResult] = useState(null);

  const scan = async () => {
    setBusy(true); setError(''); setAppliedResult(null);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/cleanup/unplan-sub-minute`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setPreview(data);
    } catch (e) { setError(e.message || String(e)); }
    finally { setBusy(false); }
  };

  const apply = async () => {
    if (!preview || (preview.candidates || []).length === 0) return;
    const n = preview.stats.candidates;
    if (!window.confirm(`Permanently DELETE ${n} short Unplan Visit row${n === 1 ? '' : 's'} (< 5 min)?\n\nThese are mostly GPS jitter false positives. Admin-actioned rows are skipped. This action cannot be undone.`)) return;
    setApplying(true); setError('');
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/admin/cleanup/unplan-sub-minute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirm: true }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setAppliedResult(data);
      setPreview(null);
      if (onApplied) onApplied();
    } catch (e) { setError(e.message || String(e)); }
    finally { setApplying(false); }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="unplan-sub-minute-card">
      <div className="flex items-start gap-2 flex-wrap">
        <div className="flex-1 text-sm">
          <div className="font-semibold text-amber-900">Cleanup — delete short Unplan Visits (&lt; 5 min)</div>
          <div className="text-xs text-amber-800 mt-0.5">
            Permanently removes pending Unplan Visit rows that lasted less than <strong>5 minutes</strong> (mostly the <strong>when_left == when_returned</strong> jitter rows with DURATION = &quot;—&quot;). Runtime gates now block new ones; this purges the historical backlog. Admin-actioned rows are preserved.
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={scan}
            disabled={busy || applying}
            className="px-3 py-1.5 text-sm bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 rounded-lg flex items-center gap-1"
            data-testid="unplan-sub-minute-scan-btn"
          >
            {busy ? '…' : 'Scan (dry run)'}
          </button>
          {preview && (preview.stats?.candidates ?? 0) > 0 && (
            <button
              onClick={apply}
              disabled={applying}
              className="px-3 py-1.5 text-sm bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white rounded-lg flex items-center gap-1"
              data-testid="unplan-sub-minute-apply-btn"
            >
              {applying ? 'Deleting…' : `Delete (${preview.stats.candidates})`}
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mt-2">{error}</div>}

      {preview && (
        <div className="mt-3 text-xs">
          <div className="text-amber-900">
            Found <strong>{preview.stats.candidates}</strong> sub-minute false-positive row{preview.stats.candidates === 1 ? '' : 's'} eligible for deletion.
          </div>
        </div>
      )}

      {appliedResult && (
        <div className="mt-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded p-2" data-testid="unplan-sub-minute-applied">
          Deleted <strong>{appliedResult.deleted}</strong> sub-minute false-positive{appliedResult.deleted === 1 ? '' : 's'}. New rows are blocked at the GPS level — no more &quot;same minute&quot; entries should appear going forward.
        </div>
      )}
    </div>
  );
}
